const SCORE_WEIGHTS = {
  range: 0.08,
  payload: 0.08,
  motor: 0.08,
  comfort: 0.08,
  safety: 0.05,
  service: 0.08,
  value: 0.55
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function normalise(value, min, max) {
  if (max === min) return 0;
  return clamp((value - min) / (max - min), 0, 1) * 100;
}

function calculateScore(scooter) {
  const rangeScore = normalise(scooter.rangeKm, 25, 110);
  const payloadScore = normalise(scooter.payloadKg, 100, 160);
  const motorScore = normalise(scooter.peakPowerW || scooter.nominalPowerW, 500, 1600);
  const comfortScore = clamp((scooter.comfortScore || 0) * 10, 0, 100);
  const safetyScore = clamp((scooter.safetyScore || 0) * 10, 0, 100);
  const serviceScore = clamp((scooter.serviceScore || 0) * 10, 0, 100);
  const expectedPrice = 350 + scooter.rangeKm * 10 + scooter.payloadKg * 3 + (scooter.peakPowerW || scooter.nominalPowerW) * 0.22;
  const valueScore = clamp((expectedPrice / scooter.priceEur) * 100, 0, 100);
  const premiumPenalty = Math.max(0, (scooter.priceEur - 1200) / 75);

  const raw =
    rangeScore * SCORE_WEIGHTS.range +
    payloadScore * SCORE_WEIGHTS.payload +
    motorScore * SCORE_WEIGHTS.motor +
    comfortScore * SCORE_WEIGHTS.comfort +
    safetyScore * SCORE_WEIGHTS.safety +
    serviceScore * SCORE_WEIGHTS.service +
    valueScore * SCORE_WEIGHTS.value -
    premiumPenalty;

  return Math.round(raw);
}

function includesText(scooter, query) {
  if (!query) return true;
  const haystack = `${scooter.brand} ${scooter.model} ${scooter.badges?.join(" ") || ""}`.toLowerCase();
  return haystack.includes(query.trim().toLowerCase());
}

function matchesList(value, list) {
  return !list || list.length === 0 || list.includes(value);
}

function filterScooters(scooters, filters = {}) {
  return scooters.filter((scooter) => {
    if (!includesText(scooter, filters.query)) return false;
    if (filters.streetLegalOnly && !scooter.streetLegal) return false;
    if (filters.availableOnly && !scooter.available) return false;
    if (filters.minRangeKm && scooter.rangeKm < filters.minRangeKm) return false;
    if (filters.maxPriceEur && scooter.priceEur > filters.maxPriceEur) return false;
    if (filters.minPayloadKg && scooter.payloadKg < filters.minPayloadKg) return false;
    if (!matchesList(scooter.brand, filters.brands)) return false;
    if (!matchesList(scooter.suspension, filters.suspension)) return false;
    if (!matchesList(scooter.tireSizeInch, filters.tireSizes)) return false;
    if (!matchesList(scooter.tireType, filters.tireTypes)) return false;
    if (filters.minAfterSalesScore && (scooter.afterSalesScore || 0) < filters.minAfterSalesScore) return false;
    if (filters.minPartsAvailabilityScore && (scooter.partsAvailabilityScore || 0) < filters.minPartsAvailabilityScore) return false;
    return true;
  });
}

function sortScooters(scooters, sortKey = "brand-asc") {
  const sorted = [...scooters];
  const comparators = {
    "score-desc": (a, b) => calculateScore(b) - calculateScore(a),
    "price-asc": (a, b) => a.priceEur - b.priceEur,
    "range-desc": (a, b) => b.rangeKm - a.rangeKm,
    "weight-asc": (a, b) => a.weightKg - b.weightKg,
    "brand-asc": (a, b) => `${a.brand} ${a.model}`.localeCompare(`${b.brand} ${b.model}`, "de")
  };

  return sorted.sort(comparators[sortKey] || comparators["brand-asc"]);
}

function getRecommendedScooters(scooters) {
  const ranked = sortScooters(scooters, "score-desc");
  const bestRange = [...scooters].sort((a, b) => b.rangeKm - a.rangeKm)[0];
  const bestValue = [...scooters].sort((a, b) => {
    const aValue = calculateScore(a) / a.priceEur;
    const bValue = calculateScore(b) / b.priceEur;
    return bValue - aValue;
  })[0];

  return [
    { badge: "Testsieger", reason: "Bester Gesamt-Score", scooter: ranked[0] },
    { badge: "Beste Reichweite", reason: `${bestRange.rangeKm} km Herstellerangabe`, scooter: bestRange },
    { badge: "Preis-Leistung", reason: "Starker Score pro Euro", scooter: bestValue }
  ];
}

function getCta(scooter) {
  if (scooter.available && scooter.shopUrl) {
    return {
      primaryLabel: "Zum Produkt",
      primaryUrl: scooter.shopUrl,
      secondaryLabel: "Probefahrt anfragen",
      availabilityLabel: "Sofort verfügbar",
      kind: "shop"
    };
  }

  if (scooter.shopUrl) {
    return {
      primaryLabel: "Mehr Infos",
      primaryUrl: scooter.shopUrl,
      secondaryLabel: "Alternative anzeigen",
      availabilityLabel: "Vergleichsmodell",
      kind: "external"
    };
  }

  return {
    primaryLabel: "Alternative anzeigen",
    primaryUrl: "#alternativen",
    secondaryLabel: "Benachrichtigen lassen",
    availabilityLabel: "Vergleichsmodell",
    kind: "comparison"
  };
}

function buildStructuredData(scooters, pageUrl) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    url: pageUrl,
    name: "E-Scooter Vergleich",
    itemListElement: scooters.map((scooter, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Product",
        name: `${scooter.brand} ${scooter.model}`,
        brand: { "@type": "Brand", name: scooter.brand },
        category: "E-Scooter",
        offers: {
          "@type": "Offer",
          priceCurrency: "EUR",
          price: scooter.priceEur,
          availability: scooter.available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          url: scooter.shopUrl || pageUrl
        },
        additionalProperty: [
          { "@type": "PropertyValue", name: "Reichweite", value: `${scooter.rangeKm} km` },
          { "@type": "PropertyValue", name: "Zuladung", value: `${scooter.payloadKg} kg` },
          { "@type": "PropertyValue", name: "Straßenzulassung", value: scooter.streetLegal ? "Ja" : "Nein" },
          { "@type": "PropertyValue", name: "Maximalgeschwindigkeit", value: scooter.maxSpeedKmh ? `${scooter.maxSpeedKmh} km/h` : "Keine Angabe" },
          { "@type": "PropertyValue", name: "Steigung", value: scooter.climbPercent ? `${scooter.climbPercent} %` : "Keine Angabe" },
          { "@type": "PropertyValue", name: "Blinker", value: typeof scooter.hasIndicators === "boolean" ? (scooter.hasIndicators ? "Ja" : "Nein") : "Keine Angabe" }
        ]
      }
    }))
  };
}

const scooterComparisonApi = {
  calculateScore,
  filterScooters,
  sortScooters,
  getRecommendedScooters,
  getCta,
  buildStructuredData
};

if (typeof module !== "undefined" && module.exports) {
  module.exports = scooterComparisonApi;
}

if (typeof window !== "undefined") {
  window.ScooterComparison = scooterComparisonApi;
}
